import { useCallback, useEffect, useState } from "react";
import { segColor, segText } from "../helpers/constants";
import type { Segment, SegType } from "../helpers/types";
import { getTimeLine } from "../../../../api/controllerDashboardApis"
import { recordingSocket } from "../../../../components/mediasoup/recordingSocket";


function calcNowPct() {
  const now = new Date();
  const start = 9 * 60;
  const end = 17 * 60;
  const cur = now.getHours() * 60 + now.getMinutes();
  return Math.min(Math.max((cur - start) / (end - start), 0), 1);
}

function getSegmentLabel(t: SegType | "N"): string {
  if (t === "l") return "Live";
  if (t === "f") return "Flagged";
  if (t === "r") return "Recess";
  return "Rec_only";
}

const LEGEND_ITEMS: { type: SegType; label: string; color: string }[] = [
  { type: "l",  label: "Recorded + Streaming", color: "#22C55E" },
  { type: "f",  label: "Flagged",              color: "#EF4444" },
  { type: "r",  label: "Recess / Gap",         color: "#F59E0B" },
  { type: "rc", label: "Recording only",       color: "#A78BFA" },
];

interface TooltipState {
  x: number;
  y: number;
  rm: string;
  cs: string;
  seg: Segment;
}

export function Timeline({
  onFlagSegmentClick,
  onEditArchive,
}: Readonly<{
  onFlagSegmentClick?: (flagId?: string) => void;
  onEditArchive?: () => void;
}>) {
  const [nowPct, setNowPct] = useState(calcNowPct);

  const [timelineRows, setTimelineRows] = useState<
    { rm: string; cs: string; segs: Segment[] }[]
  >([]);

  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  useEffect(() => {
    const id = setInterval(() => setNowPct(calcNowPct()), 60_000);
    return () => clearInterval(id);
  }, []);

  const handleSegMouseMove = useCallback(
    (e: React.MouseEvent, rm: string, cs: string, seg: Segment) => {
      setTooltip({ x: e.clientX, y: e.clientY, rm, cs, seg });
    },
    []
  );

  const handleSegMouseLeave = useCallback(() => setTooltip(null), []);

  const handleFlagClick = useCallback(
    (seg: Segment) => {
      document
        .getElementById("flagPan")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });

      onFlagSegmentClick?.(seg.flagId);
    },
    [onFlagSegmentClick]
  );
  const loadTimeline = useCallback(async () => {
    try {
      const res = await getTimeLine();
      const data = res?.data ?? res;
      const rows =
        data?.todayRecordingTimeline?.map((room: any) => {
          const segmentsRaw = room?.segments ?? [];
      const segs: Segment[] = segmentsRaw.map((seg: any) => {
            let type: SegType = "r";
            switch (seg?.type) {
              case "recorded+streaming":
                type = "l";
                break;
              case "flagged":
                type = "f";
                break;
              case "recess":
                type = "r";
                break;
              case "rec_only":
                type = "rc" as SegType;
                break;
              default:
                type = "r";
            }

            const start = seg?.startTime
              ? new Date(seg.startTime)
              : null;

            const end = seg?.endTime
              ? new Date(seg.endTime)
              : null;
            const flagReason =
              seg?.type === "flagged"
                ? (seg?.flagSubType ?? seg?.flagType ?? undefined)
                : undefined;

            return {
              t: type,
              f: Math.max(
                (seg?.offsetEnd ?? 1) - (seg?.offsetStart ?? 0),
                1
              ),
              lb:
                seg?.type === "flagged"
                  ? "⚑"
                  : seg?.label ?? "",

              ti:
                start && end
                  ? `${start.toLocaleTimeString()} - ${end.toLocaleTimeString()}`
                  : start
                  ? `${start.toLocaleTimeString()}`
                  : "",

              flagId: seg?.flagId,
              flagReason,
            };
          });

          return {
            rm: room?.courtRoomId ?? "",
            cs: room?.cnr ?? "",
            segs,
          };
        }) ?? [];

      setTimelineRows(rows);
    } catch (err) {
      console.error("Timeline error", err);
    }
  }, []);

  useEffect(() => {
    loadTimeline();

  console.log("Timeline mounted, socket connected:", recordingSocket.connected);

  const handleTimelineUpdate = (payload: any) => {
     console.log("timeline_update received, payload:", payload);
    loadTimeline();
  };

  recordingSocket.on("timeline_update", handleTimelineUpdate);

  return () => {
    recordingSocket.off("timeline_update", handleTimelineUpdate);
  };
}, [loadTimeline]);

  

  return (
    <div className="bg-white border border-gray-200 rounded-[10px] overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
        <div className="text-[13px] font-medium text-gray-800">
          Today's Recordings
        </div>

        <button
          type="button"
          className="text-[11px] text-blue-600 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={onEditArchive}
        >
          Edit archive ↗
        </button>
      </div>

      {/* axis */}
      <div className="flex justify-between text-[10px] text-gray-400 px-4 pt-3 pb-1">
        {["09:00", "11:00", "13:00", "15:00", "17:00"].map((t) => (
          <span key={t}>{t}</span>
        ))}
      </div>

      <div className="px-4 pb-2 space-y-2.5 relative">
        
        {timelineRows.map((d) => {
          const flagged = d.segs.some((s) => s.t === "f");

          return (
            <div key={`${d.rm}-${d.cs}`}>
              <div className="flex justify-between text-[11px] text-gray-500 mb-1">
                <span>
                  {d.rm} · {d.cs}
                </span>

                {flagged && (
                  <span className="text-red-600 text-[10px]">⚑ flagged</span>
                )}
              </div>
     
              <div className="flex h-3.5 rounded overflow-hidden gap-px bg-gray-100">
                
                {d.segs.map((s, i) => {
                  const st = s.t as SegType;

                  return (
                    <button
                      key={`${d.rm}-${d.cs}-${i}`}
                      type="button"
                      className={[
                        segColor[st],
                        segText[st],
                        "text-[7px] font-medium flex items-center justify-center",
                        "overflow-hidden whitespace-nowrap rounded-sm",
                      ].join(" ")}
                      style={{ flex: s.f }}
                      onMouseMove={(e) =>
                        handleSegMouseMove(e, d.rm, d.cs, s)
                      }
                      onMouseLeave={handleSegMouseLeave}
                      onClick={() => {
                        if (s.t === "f") handleFlagClick(s);
                      }}
                    >
                      {s.lb ?? ""}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* live line */}
        <div
          className="absolute top-0 bottom-0 pointer-events-none"
          style={{
            left: `${nowPct * 100}%`,
            width: 2,
            background: "#1A56DB",
          }}
        />
      </div>

      {/* FIX: color legend — required by AC, was missing entirely */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-2 border-t border-gray-100 text-[10px] text-gray-500">
        {LEGEND_ITEMS.map((item) => (
          <span key={item.type} className="flex items-center gap-1">
            <span
              className="w-2 h-2 rounded-sm shrink-0"
              style={{ background: item.color }}
              aria-hidden="true"
            />
            {item.label}
          </span>
        ))}
      </div>

      {/* tooltip */}
      {tooltip && (
        <div
          className="fixed z-50 bg-gray-900 text-white text-[11px] px-2.5 py-1.5 rounded shadow-lg pointer-events-none"
          style={{ left: tooltip.x + 12, top: tooltip.y - 8 }}
        >
          <div className="font-medium">
            {tooltip.rm} · {tooltip.cs}
          </div>

          <div className="text-white/60 text-[10px] mt-0.5 capitalize">
            {getSegmentLabel(tooltip.seg.t)}
          </div>

          <div className="text-white/50 text-[10px]">
            {tooltip.seg.ti}
          </div>

          {tooltip.seg.t === "f" && (
            <>
              {tooltip.seg.flagReason && (
                <div className="text-amber-300 text-[10px] mt-0.5">
                  {tooltip.seg.flagReason}
                </div>
              )}
              <div className="text-red-400 text-[10px] mt-0.5">
                ⚑ Click to view flag
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}